import { Flex, FlexItem, PageSection, Split, SplitItem, Title, TitleProps } from '@patternfly/react-core';
import * as React from 'react';
import { createPortal } from 'react-dom';

/**
 * The title bar every detail page wears: the page's name on the left, its Save/Cancel/kebab on
 * the right, and whatever labels belong beside the name in between. It stays put while the page
 * scrolls, so the name of the record being edited and the buttons that save it are never more
 * than a glance away.
 *
 * It renders through a portal rather than where the page puts it. Staying visible means sitting
 * inside the same sticky PageGroup as the breadcrumb, which the layout owns - and that group is
 * the whole point: one sticky box holding both bars keeps them flush against each other at every
 * density and window size. Pinning a second, separate sticky element under the breadcrumb needs
 * an offset equal to the breadcrumb's height, and any figure hard-coded there is wrong the moment
 * condensed view, a longer trail or a narrower window changes that height - too small and the
 * title covers the fields below it, too large and page content scrolls through the gap between
 * the two. Rendering into the group removes the offset, and with it both failures.
 *
 * The buttons still belong to the page, so this stays an ordinary component the page renders with
 * its own handlers and state; only the DOM node it lands in comes from the layout.
 */

/** Where the layout keeps room for the title bar. Written by PageHeaderSlot, read by the portal. */
const PAGE_HEADER_SLOT_ID = 'cas-page-header-slot';

/** The layout's half of the arrangement: the empty box, directly under the breadcrumb. */
export const PageHeaderSlot: React.FunctionComponent = () => <div id={PAGE_HEADER_SLOT_ID} />;

interface DetailPageHeaderProps {
  /** The page title. Rendered as the page's only h1. */
  title: React.ReactNode;
  /** Smaller line under the title, for a second name or description. */
  subtitle?: React.ReactNode;
  /** Buttons and menus for the right-hand end, normally an ActionList. */
  actions?: React.ReactNode;
  /**
   * Labels shown beside the title - status, type, flags. FlexItems, laid out in the same row as
   * the title.
   */
  children?: React.ReactNode;
  /** Departs from the standard page title size. Only for pages that are not top-level records. */
  titleSize?: TitleProps['size'];
}

export const DetailPageHeader: React.FunctionComponent<DetailPageHeaderProps> = ({
  title,
  subtitle,
  actions,
  children,
  titleSize = '4xl'
}) => {
  // The slot is the layout's, so it exists only once the layout has committed - which it has by
  // the time a layout effect runs, and React re-renders on this state before the browser paints,
  // so the title does not flash in a frame late.
  const [slot, setSlot] = React.useState<HTMLElement | null>(null);
  React.useLayoutEffect(() => {
    setSlot(document.getElementById(PAGE_HEADER_SLOT_ID));
  }, []);

  const header = (
    <PageSection isWidthLimited isFilled={false}>
      <Split>
        <SplitItem>
          <Flex
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
            flexWrap={{ default: 'nowrap' }}
          >
            <FlexItem>
              <Title headingLevel="h1" size={titleSize}>{title}</Title>
            </FlexItem>
            {children}
          </Flex>
          {subtitle && <Title headingLevel="h2" size="2xl">{subtitle}</Title>}
        </SplitItem>
        <SplitItem isFilled />
        <SplitItem>{actions}</SplitItem>
      </Split>
    </PageSection>
  );

  // Rendered where the page puts it when there is no slot to move into - a test mounting the page
  // on its own, or any future host without this layout. The title and the Save button are too
  // much of the page to drop on the floor because the box they prefer is missing; they only lose
  // the sticking.
  return slot ? createPortal(header, slot) : header;
};
